import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CssBaseline, Box } from "@mui/material";
import { AuthProvider, useAuth } from "./utils/authContext";
import { StreamingProvider } from "./contexts/StreamingContext";
import { SidebarContext } from "./contexts/SidebarContext";
import { DocumentSectionsProvider } from "./contexts/DocumentSectionsContext";
import { ThemeContextProvider } from "./contexts/ThemeContext";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { disableUserback, initializeUserback } from "./utils/userback";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./utils/firebase";
import { getUserSettings } from "./utils/firestoreService";
import { meetsMinRole } from "./config/featureFlagDefinitions";
import ReactGA from "react-ga4";
import { googleAnalytics } from "./globals";
import { initializeBrowserAnalytics, shouldEnableAnalytics } from "./utils/emailVerification";

import "./css/Global.css";

//main pages
import Login from "./Login";
import Signup from "./Signup";
import AuthAction from "./AuthAction";
import ResetPassword from "./ResetPassword";
import Dashboard from "./Dashboard";
import Patients from "./Patients";
import Document from "./Document";
import Patient from "./Patient";
import Settings from "./Settings";
import Account from "./Account";
import AppShell from "./AppShell";
import Admin from "./Admin";
import AdminPromptBuilder from "./admin/AdminPromptBuilder";
import CompanyAdmin from "./CompanyAdmin";
import InviteAccept from "./InviteAccept";

const PrivateRoute = ({ children }) => {
  const { currentUser, isVerifying } = useAuth();

  // Show loading if still verifying authentication
  if (isVerifying) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <div>Verifying authentication...</div>
      </Box>
    );
  }

  // Ensure user is fully authenticated and verified
  // authContext already handles verification checks
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RoleBasedRoute = ({ children, minRole = "manager" }) => {
  const { currentUser, isVerifying } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          } else {
            // Default to standard role if no user document
            setUserData({ userRole: "standard" });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          // Default to standard role if fetch fails
          setUserData({ userRole: "standard" });
        }
      }
      setIsLoading(false);
    };

    if (currentUser) {
      fetchUserData();
    } else if (!isVerifying) {
      setIsLoading(false);
    }
  }, [currentUser, isVerifying]);

  // Show loading while verifying auth or fetching user data
  if (isVerifying || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <div>Loading...</div>
      </Box>
    );
  }

  // Redirect if not authenticated
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Check if user meets minimum role
  const userRole = userData?.userRole || "standard";
  if (!meetsMinRole(userRole, minRole)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column">
        <div style={{ fontSize: "var(--text-xl)", marginBottom: "16px" }}>Access Denied</div>
        <div style={{ color: "var(--color-text-light)", marginBottom: "24px" }}>You don't have permission to access this page.</div>
        <button onClick={() => window.history.back()} style={{ padding: "8px 16px", cursor: "pointer" }}>
          Go Back
        </button>
      </Box>
    );
  }

  return children;
};

// Theme is now managed by ThemeContext

const AppLayout = ({ children }) => {
  const location = useLocation();
  const { currentUser, isVerifying } = useAuth();
  const hideAppShell = location.pathname === "/" || location.pathname === "/signup" || location.pathname === "/__/auth/action" || location.pathname === "/reset-password" || location.pathname.startsWith("/invite/");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [processingDocId, setProcessingDocId] = useState(null);
  const [isActivelyEditing, setIsActivelyEditing] = useState(false);
  const [debouncedIsActivelyEditing, setDebouncedIsActivelyEditing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activityBarPosition, setActivityBarPosition] = useState(null);
  const [activityBarDismissed, setActivityBarDismissed] = useState(null);
  const activeEditorsRef = useRef(new Set());

  // Load user's sidebar preference on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (currentUser?.uid) {
        try {
          const userSettings = await getUserSettings(currentUser.uid);
          if (userSettings?.sidebarOpen === false) {
            setSidebarCollapsed(true);
          }
          if (userSettings?.activityBarPosition) {
            setActivityBarPosition(userSettings.activityBarPosition);
          }
          if (userSettings?.activityBarDismissed) {
            setActivityBarDismissed(userSettings.activityBarDismissed);
          }
        } catch (error) {
          console.error("Error loading user preferences:", error);
        }
      }
    };
    loadUserPreferences();
  }, [currentUser?.uid]);

  // Function to update editing state from any editor
  const updateEditingState = useCallback((editorId, isEditing) => {
    if (isEditing) {
      activeEditorsRef.current.add(editorId);
    } else {
      activeEditorsRef.current.delete(editorId);
    }

    const hasActiveEditors = activeEditorsRef.current.size > 0;

    setIsActivelyEditing(hasActiveEditors);
  }, []);

  // Debounce isActivelyEditing to prevent rapid toggling
  useEffect(() => {
    const timer = setTimeout(() => {
      const wasEditing = debouncedIsActivelyEditing;
      const nowEditing = isActivelyEditing;

      setDebouncedIsActivelyEditing(isActivelyEditing);

      // Track editing state changes for GA
      if (nowEditing !== wasEditing) {
        if (shouldEnableAnalytics()) {
          ReactGA.event({
            category: "Document",
            action: nowEditing ? "Editing Started" : "Editing Stopped",
            label: window.location.pathname,
          });
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isActivelyEditing, debouncedIsActivelyEditing]);

  // Prevent any content rendering for unauthenticated users on protected routes
  const isProtectedRoute = !["/", "/signup", "/__/auth/action", "/reset-password"].includes(location.pathname) && !location.pathname.startsWith("/invite/");
  if (isProtectedRoute && !currentUser && !isVerifying) {
    return <Navigate to="/" replace />;
  }

  return (
    <SidebarContext.Provider
      value={{
        collapsed: sidebarCollapsed,
        setSidebarCollapsed,
        isActivelyEditing: debouncedIsActivelyEditing,
        setIsActivelyEditing: updateEditingState,
        isProcessing,
        setIsProcessing,
        processingDocId,
        setProcessingDocId,
      }}>
      <Box sx={{ display: "block" }}>
        <StreamingProvider>
          <DocumentSectionsProvider>
            {!hideAppShell && (
              <AppShell activityBarPosition={activityBarPosition} activityBarDismissed={activityBarDismissed}>
                {children}
              </AppShell>
            )}
            {hideAppShell && (
              <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                {children}
              </Box>
            )}
          </DocumentSectionsProvider>
        </StreamingProvider>
      </Box>
    </SidebarContext.Provider>
  );
};

const AppContent = () => {
  const { currentUser } = useAuth();
  // Theme preference is now managed by ThemeContext
  return (
    <Router>
      <CssBaseline />
      <AppLayout>
        <Routes>
          <Route path="/__/auth/action" element={<AuthAction />} />
          <Route path="/" element={currentUser ? <Navigate to="/dashboard" replace /> : <Login />} />
          <Route path="/signup" element={currentUser ? <Navigate to="/dashboard" replace /> : <Signup />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/patient"
            element={
              <PrivateRoute>
                <Patient />
              </PrivateRoute>
            }
          />
          <Route
            path="/patients"
            element={
              <PrivateRoute>
                <Patients />
              </PrivateRoute>
            }
          />
          <Route
            path="/document"
            element={
              <PrivateRoute>
                <Document />
              </PrivateRoute>
            }
          />
          <Route
            path="/account"
            element={
              <PrivateRoute>
                <Account />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <RoleBasedRoute minRole="manager">
                <Settings />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <RoleBasedRoute minRole="superAdmin">
                <Admin />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin/:adminTab"
            element={
              <RoleBasedRoute minRole="superAdmin">
                <Admin />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin/data/:dataTab"
            element={
              <RoleBasedRoute minRole="superAdmin">
                <Admin />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin/prompts/new"
            element={
              <RoleBasedRoute minRole="superAdmin">
                <AdminPromptBuilder />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/admin/prompts/edit/:promptId"
            element={
              <RoleBasedRoute minRole="superAdmin">
                <AdminPromptBuilder />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/company-admin"
            element={
              <RoleBasedRoute minRole="companyAdmin">
                <CompanyAdmin />
              </RoleBasedRoute>
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Routes>
      </AppLayout>
    </Router>
  );
};

const App = () => {
  // Initialize Google Analytics only after scrubbing action URLs and checking environment/route rules
  useEffect(() => {
    if (initializeBrowserAnalytics({ reactGA: ReactGA, measurementId: googleAnalytics })) {
      console.log("Google Analytics initialized");
    } else {
      console.log("Google Analytics disabled");
    }
  }, []);

  // Error handling for chunk/asset loading issues
  useEffect(() => {
    // Handle chunk/asset loading errors
    const handleAssetError = (event) => {
      const isAssetError =
        event.message?.includes('Unexpected token') ||
        event.message?.includes('SyntaxError') ||
        event.filename?.match(/\.(js|css)$/);

      if (isAssetError) {
        const reloadAttempted = sessionStorage.getItem('asset_reload_time');
        const now = Date.now();

        // Only reload once per 10 seconds
        if (!reloadAttempted || (now - parseInt(reloadAttempted)) > 10000) {
          console.log('Asset loading error detected, reloading...');
          sessionStorage.setItem('asset_reload_time', now.toString());
          window.location.reload();
        } else {
          // If we already reloaded recently, show error instead
          console.error('Persistent asset loading error - please clear cache');
        }
      }
    };

    window.addEventListener('error', handleAssetError);

    // Clear flag after successful render
    const timer = setTimeout(() => {
      sessionStorage.removeItem('asset_reload_time');
    }, 5000);

    return () => {
      window.removeEventListener('error', handleAssetError);
      clearTimeout(timer);
    };
  }, []);

  return (
    <AuthProvider>
      <FeatureFlagProvider>
        <ThemeContextProvider>
          <UserbackInitializer />
          <AppContent />
        </ThemeContextProvider>
      </FeatureFlagProvider>
    </AuthProvider>
  );
};

const UserbackInitializer = () => {
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      disableUserback();
      return undefined;
    }

    let cancelled = false;

    // Fetch Firestore profile so Userback receives role, company & billing context
    const loadProfile = async () => {
      try {
        const { getDocument } = await import('./utils/firestoreService');
        const profile = await getDocument('users', currentUser.uid) || {};
        if (!cancelled) initializeUserback(currentUser, profile);
      } catch {
        // Fall back to basic init if profile fetch fails
        if (!cancelled) initializeUserback(currentUser);
      }
    };
    loadProfile();

    return () => {
      cancelled = true;
      disableUserback();
    };
  }, [currentUser]);

  return null;
};

export default App;
